import React from 'react';
import { Box, AppBar, Toolbar, Drawer, List, ListItem, ListItemIcon, ListItemText, Avatar, Menu, MenuItem, Chip, Typography, IconButton, Badge, Divider, Tooltip } from '@mui/material';
import { Logout, Settings, Dashboard, People, CardGiftcard, EmojiEvents, BarChart, Spa, Notifications, Search, LightMode, DarkMode } from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { useThemeMode } from '../context/ThemeContext';
import Cookies from 'js-cookie';

const DRAWER_WIDTH = 240;

const menuItems = [
  { label: 'Dashboard', path: '/dashboard', icon: Dashboard },
  { label: 'Rewards', path: '/rewards', icon: CardGiftcard },
  { label: 'Challenges', path: '/challenges', icon: EmojiEvents },
  { label: 'Partners', path: '/partners', icon: People },
  { label: 'Business Intelligence', path: '/reports', icon: BarChart }
];

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth.user);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();
  const { mode, toggleTheme, isDark, colors } = useThemeMode();

  // Theme-aware colors
  const themeColors = {
    sidebarBg: isDark 
      ? 'linear-gradient(180deg, #0a0f1a 0%, #111827 100%)' 
      : 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
    mainBg: isDark ? '#0f172a' : '#f8fafc',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    appBarBg: isDark ? 'rgba(30,41,59,0.95)' : 'rgba(255,255,255,0.95)',
    textPrimary: isDark ? '#f1f5f9' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    Cookies.remove('authToken');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: themeColors.mainBg }}>
      {/* Sidebar - Fixed */}
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            background: themeColors.sidebarBg,
            color: '#fff',
            border: 'none',
            borderRadius: '0 24px 24px 0'
          }
        }}
        variant="permanent"
        anchor="left"
      >
        {/* Logo Header */}
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            component="img"
            src="/caldea-logo-white.png"
            alt="Caldea"
            sx={{ height: 32, objectFit: 'contain', filter: 'brightness(1.2)' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff', lineHeight: 1.2 }}>
              Caldea Club
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem', letterSpacing: 0.5 }}>
              Powered by PEpL
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />

        {/* Navigation */}
        <Box sx={{ px: 1.5, py: 2 }}>
          <Typography variant="caption" sx={{ 
            color: '#64748b', 
            fontWeight: 600, 
            letterSpacing: '0.1em', 
            fontSize: '0.65rem',
            px: 1.5,
            mb: 1,
            display: 'block'
          }}>
            MENU PRINCIPAL
          </Typography>
          
          <List sx={{ pt: 0.5 }}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <ListItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    py: 1.2,
                    px: 1.5,
                    background: isActive 
                      ? `linear-gradient(135deg, rgba(29,202,255,0.2) 0%, rgba(29,202,255,0.1) 100%)`
                      : 'transparent',
                    color: isActive ? colors.primary : '#94a3b8',
                    borderLeft: isActive ? `3px solid ${colors.primary}` : '3px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: isActive 
                        ? `linear-gradient(135deg, rgba(29,202,255,0.25) 0%, rgba(29,202,255,0.15) 100%)`
                        : 'rgba(255,255,255,0.04)',
                      color: isActive ? colors.primary : '#e2e8f0'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                    <Icon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ 
                      fontSize: '0.85rem', 
                      fontWeight: isActive ? 600 : 400,
                      letterSpacing: '-0.01em'
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Piloto Status Card */}
        <Box sx={{ mt: 'auto', p: 2 }}>
          <Box sx={{ 
            p: 2, 
            background: `linear-gradient(135deg, rgba(29,202,255,0.15) 0%, rgba(29,202,255,0.05) 100%)`,
            borderRadius: 3,
            border: `1px solid rgba(29,202,255,0.2)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors.success, animation: 'pulse 2s infinite' }} />
              <Typography variant="caption" sx={{ color: colors.primary, fontWeight: 600, letterSpacing: '0.05em' }}>
                PILOTO ACTIVO
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
              Febrero 2026 • 28 días
            </Typography>
            <Chip 
              label="Nivel A" 
              size="small" 
              sx={{ 
                mt: 1.5,
                bgcolor: `rgba(29,202,255,0.2)`, 
                color: colors.primary, 
                fontSize: '0.7rem',
                fontWeight: 600,
                height: 22
              }} 
            />
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top AppBar */}
        <AppBar 
          position="sticky" 
          elevation={0}
          sx={{ 
            backgroundColor: themeColors.appBarBg,
            backdropFilter: 'blur(20px)',
            borderBottom: `1px solid ${themeColors.cardBorder}`,
            color: themeColors.textPrimary
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: themeColors.textPrimary, fontSize: '1.1rem' }}>
                {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
              </Typography>
              <Chip 
                label="Demo" 
                size="small" 
                sx={{ 
                  bgcolor: isDark ? 'rgba(34,197,94,0.2)' : '#f0fdf4', 
                  color: colors.success,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 24
                }} 
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                icon={<Spa sx={{ color: `${colors.primary} !important`, fontSize: 16 }} />} 
                label="32 canjes hoy" 
                size="small" 
                sx={{ 
                  bgcolor: isDark ? 'rgba(29,202,255,0.2)' : 'rgba(29,202,255,0.1)', 
                  color: colors.primary,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  '& .MuiChip-icon': { ml: 0.5 }
                }} 
              />
              
              {/* Theme Toggle Button */}
              <Tooltip title={isDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}>
                <IconButton 
                  onClick={toggleTheme}
                  size="small" 
                  sx={{ 
                    color: themeColors.textSecondary,
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                      color: colors.primary
                    }
                  }}
                >
                  {isDark ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                </IconButton>
              </Tooltip>
              
              <IconButton size="small" sx={{ color: themeColors.textSecondary }}>
                <Search fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: themeColors.textSecondary }}>
                <Badge badgeContent={3} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
                  <Notifications fontSize="small" />
                </Badge>
              </IconButton>
              <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: themeColors.cardBorder }} />
              <Box 
                onClick={handleMenuOpen}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 2,
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }
                }}
              >
                <Avatar sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: colors.primary,
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}>
                  {user?.name?.[0] || 'A'}
                </Avatar>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2, color: themeColors.textPrimary }}>
                    {user?.name || 'Admin'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: themeColors.textSecondary, fontSize: '0.7rem' }}>
                    Operador
                  </Typography>
                </Box>
              </Box>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.12)',
                    minWidth: 180,
                    bgcolor: themeColors.cardBg,
                    border: `1px solid ${themeColors.cardBorder}`
                  }
                }}
              >
                <MenuItem sx={{ fontSize: '0.85rem', color: themeColors.textPrimary }}>
                  <Settings fontSize="small" sx={{ mr: 1.5, color: themeColors.textSecondary }} />
                  Configuración
                </MenuItem>
                <MenuItem onClick={handleLogout} sx={{ fontSize: '0.85rem', color: colors.error }}>
                  <Logout fontSize="small" sx={{ mr: 1.5 }} />
                  Cerrar sesión
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: themeColors.mainBg,
            minHeight: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
